/**
 * MenuScreen Component
 *
 * Main menu with clean Pokedex-style UI.
 * Features polished cards, smooth animations, and clean iconography.
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Sparkles,
  LogOut,
  Swords,
  Box,
  Users,
  Trophy,
  Medal,
  CircleDot,
  Gift,
  Star,
  HelpCircle,
  Diamond,
  Wrench,
  Flame
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { useCareer } from '../contexts/CareerContext';
import { useInventory } from '../contexts/InventoryContext';
import { useTutorial } from '../contexts/TutorialContext';
import packageJson from '../../package.json';
import { generatePokemonSprite } from '../utils/gameUtils';
import { TYPE_COLORS } from '../components/TypeIcon';
import { POKEMON } from '../shared/gameData';
import { MenuTile } from '../components/ui/menu-tile';
import ProfileIcon from '../components/ProfileIcon';
import { apiUpdateProfileIcon } from '../services/apiService';
import TutorialOverlay from '../components/TutorialOverlay';
import TutorialHighlight from '../components/TutorialHighlight';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0 }
};

// Menu configuration
const MENU_ITEMS = [
  {
    key: 'career',
    label: 'New Career',
    icon: Swords,
    color: '#E3350D',
    screen: 'pokemonSelect'
  },
  {
    key: 'pokemon',
    label: 'My Pokemon',
    icon: Box,
    color: '#78C850',
    screen: 'pokemonInventory',
    countKey: 'pokemonInventory'
  },
  {
    key: 'supports',
    label: 'Supports',
    icon: Users,
    color: '#6890F0',
    screen: 'supportInventory',
    countKey: 'supportInventory'
  },
  {
    key: 'hallOfFame',
    label: 'Hall of Fame',
    icon: Trophy,
    color: '#F8D030',
    screen: 'trainedPokemon',
    countKey: 'trainedPokemon'
  },
  {
    key: 'pokemonGacha',
    label: 'Pokemon Gacha',
    icon: CircleDot,
    color: '#A040A0',
    screen: 'gacha'
  },
  {
    key: 'supportGacha',
    label: 'Support Gacha',
    icon: Gift,
    color: '#F85888',
    screen: 'supportGacha'
  },
  {
    key: 'tournaments',
    label: 'Tournaments',
    icon: Medal,
    color: '#7038F8',
    screen: 'tournaments'
  },
  {
    key: 'pvp',
    label: 'PvP Battle',
    icon: Swords,
    color: '#C03028',
    screen: 'pvp'
  },
  {
    key: 'trials',
    label: 'Trials',
    icon: Flame,
    color: '#DC2626',
    screen: 'trials'
  }
];

// Starter selection card component
const StarterCard = ({ pokemon, name, onSelect }) => {
  const typeColor = TYPE_COLORS[pokemon.primaryType] || '#A8A878';

  return (
    <motion.button
      whileHover={{ scale: 1.03, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onSelect}
      className="pokemon-card w-full !p-0 overflow-hidden"
    >
      <div className="p-4 pb-2">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-3"
        >
          {generatePokemonSprite(pokemon.primaryType, name, 64, false)}
        </motion.div>
        <h3 className="text-pocket-text font-bold text-xs mb-2 truncate">{name}</h3>
      </div>
      <div
        className="w-full py-1 flex items-center justify-center gap-1 text-xs font-bold text-white"
        style={{ backgroundColor: typeColor }}
      >
        <img
          src={`/images/types/${pokemon.primaryType.toLowerCase()}.svg`}
          alt=""
          className="w-3.5 h-3.5"
          style={{ filter: 'brightness(0) invert(1)' }}
        />
        {pokemon.primaryType}
      </div>
    </motion.button>
  );
};

const MenuScreen = () => {
  const { user, logout, authToken, updateUser } = useAuth();
  const { setGameState } = useGame();
  const { hasActiveCareer } = useCareer();
  const {
    pokemonInventory,
    pokemonLoading,
    supportInventory,
    trainedPokemon,
    primos,
    limitBreakShards,
    loadPokemonInventory,
    addPokemon
  } = useInventory();
  const {
    tutorialActive,
    tutorialStep,
    tutorialCompleted,
    startTutorial,
    advanceStep,
    isTutorialStep,
    isLoading: tutorialLoading
  } = useTutorial();

  // Loading state to prevent flicker while career data loads
  const [isLoadingCareer, setIsLoadingCareer] = useState(true);

  // State to control starter-select tutorial overlay visibility
  const [showStarterTutorialOverlay, setShowStarterTutorialOverlay] = useState(true);

  // State to control new-career-prompt overlay vs highlight (show overlay first, then highlight)
  const [newCareerOverlayDismissed, setNewCareerOverlayDismissed] = useState(false);

  // Debug logging for tutorial state
  useEffect(() => {
    console.log('[MenuScreen] Tutorial state:', {
      tutorialLoading,
      tutorialCompleted,
      tutorialActive,
      tutorialStep,
      pokemonLoading,
      pokemonCount: pokemonInventory.length,
      isTutorialStepWelcome: isTutorialStep('welcome'),
      isTutorialStepNewCareerPrompt: isTutorialStep('new-career-prompt'),
      showWelcomeCondition: !tutorialLoading && !tutorialCompleted && !tutorialActive && tutorialStep === 'welcome'
    });
  }, [tutorialLoading, tutorialCompleted, tutorialActive, tutorialStep, pokemonLoading, pokemonInventory.length, isTutorialStep]);

  useEffect(() => {
    // Give CareerContext a moment to load active career
    const timer = setTimeout(() => setIsLoadingCareer(false), 300);
    return () => clearTimeout(timer);
  }, []);

  // Auto-start tutorial for brand new users
  useEffect(() => {
    // Wait for both pokemon inventory AND tutorial state to finish loading
    if (!pokemonLoading && !tutorialLoading && pokemonInventory.length === 0 && !tutorialCompleted && !tutorialActive) {
      console.log('[MenuScreen] New user detected, starting tutorial');
      startTutorial();
    }
  }, [pokemonLoading, tutorialLoading, pokemonInventory.length, tutorialCompleted, tutorialActive, startTutorial]);


  // Tutorial overlay continue handler
  const handleTutorialContinue = () => {
    const currentStep = tutorialStep;
    console.log('[MenuScreen] Tutorial continue from step:', currentStep);

    if (currentStep === 'welcome') {
      // Ensure tutorial is started if it wasn't already (handles race condition)
      if (!tutorialActive) {
        startTutorial();
      }
      advanceStep('starter-select');
    } else if (currentStep === 'career-goal') {
      // After career goal explanation, auto-navigate to Pokemon selection
      advanceStep('career-start');
      setGameState('pokemonSelect');
    } else if (currentStep === 'gacha-intro') {
      // Navigate to Pokemon gacha
      advanceStep('pokemon-gacha');
      setGameState('gacha');
    } else {
      advanceStep();
    }
  };

  // Counts for menu items
  const counts = {
    pokemonInventory: pokemonInventory.length,
    supportInventory: supportInventory.length,
    trainedPokemon: trainedPokemon.length
  };

  // Create dynamic menu items based on career state
  const menuItems = MENU_ITEMS.map(item => {
    if (item.key === 'career') {
      return {
        ...item,
        label: hasActiveCareer ? 'Continue Career' : 'New Career',
        screen: hasActiveCareer ? 'career' : 'pokemonSelect',
        color: hasActiveCareer ? '#10B981' : '#E3350D'
      };
    }
    return item;
  });

  // Show loading while inventory is being fetched
  if (pokemonLoading) {
    return (
      <div className="min-h-screen bg-pocket-bg flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-pocket-red/10 flex items-center justify-center">
            <Box size={32} className="text-pocket-red animate-pulse" />
          </div>
          <p className="text-pocket-text-light">Loading your collection...</p>
        </motion.div>
      </div>
    );
  }

  // Starter selection (if user has no pokemon AND inventory finished loading)
  // Don't show starter selection while loading - prevents race condition after abandon career
  if (pokemonInventory.length === 0) {
    return (
      <div className="min-h-screen bg-pocket-bg p-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="max-w-md mx-auto pt-8"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-pocket-red/10 mb-4">
              <Star size={32} className="text-pocket-red" />
            </div>
            <h1 className="text-2xl font-bold text-pocket-text mb-2">
              Choose Your Starter!
            </h1>
            <p className="text-pocket-text-light">
              Select your first Pokemon partner to begin your journey
            </p>
          </motion.div>

          {/* Starter Grid */}
          <motion.div
            variants={containerVariants}
            className="grid grid-cols-5 gap-2 starter-grid"
          >
            {['Charmander', 'Squirtle', 'Bulbasaur', 'Pikachu', 'Gastly'].map((starter) => {
              const pokemon = POKEMON[starter];
              return (
                <motion.div key={starter} variants={itemVariants}>
                  <StarterCard
                    pokemon={pokemon}
                    name={starter}
                    onSelect={async () => {
                      const result = await addPokemon(starter, pokemon);
                      if (result) {
                        // Set profile icon to match starter Pokemon
                        const iconKey = starter.toLowerCase();
                        try {
                          await apiUpdateProfileIcon(iconKey, authToken);
                          updateUser({ profileIcon: iconKey });
                        } catch (err) {
                          console.error('Failed to set starter icon:', err);
                        }
                        await loadPokemonInventory();

                        // If in tutorial, advance to career-goal step
                        if (isTutorialStep('starter-select')) {
                          advanceStep('career-goal');
                        }
                      } else {
                        alert('Failed to add starter Pokemon. Please try again.');
                      }
                    }}
                  />
                </motion.div>
              );
            })}
          </motion.div>

          {/* Version */}
          <motion.p
            variants={itemVariants}
            className="text-center text-pocket-text-light text-xs mt-8"
          >
            v{packageJson.version}
          </motion.p>
        </motion.div>

        {/* Tutorial overlays for starter selection phase */}
        {/* Show welcome overlay if tutorial is on welcome step, OR if conditions would auto-start tutorial */}
        {/* This handles the race condition where startTutorial() is called but state hasn't updated yet */}
        {(isTutorialStep('welcome') || (!tutorialLoading && !tutorialCompleted && !tutorialActive && tutorialStep === 'welcome')) && (
          <TutorialOverlay
            stepId="welcome"
            onContinue={handleTutorialContinue}
          />
        )}
        {isTutorialStep('starter-select') && showStarterTutorialOverlay && (
          <TutorialOverlay
            stepId="starter-select"
            onContinue={() => setShowStarterTutorialOverlay(false)}
          />
        )}
        {isTutorialStep('career-goal') && (
          <TutorialOverlay
            stepId="career-goal"
            onContinue={handleTutorialContinue}
          />
        )}
      </div>
    );
  }

  // Main menu
  return (
    <div className="min-h-screen bg-pocket-bg">
      {/* Header Bar */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="sticky top-0 z-10 bg-white shadow-card"
      >
        <div className="max-w-lg mx-auto flex items-center justify-between px-4 py-3">
          {/* Left side buttons */}
          <div className="flex items-center gap-2">
            {/* Profile Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setGameState('profile')}
              className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-full border border-amber-200 hover:border-amber-300 transition-colors"
            >
              <ProfileIcon
                icon={user?.profileIcon || 'pikachu'}
                size={24}
                showBorder={false}
              />
              <span className="text-pocket-text font-semibold text-sm">Profile</span>
            </motion.button>

            {/* Game Guide Button */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setGameState('guide')}
              className="p-2 bg-blue-50 rounded-full border border-blue-200 hover:border-blue-300 transition-colors"
              title="Game Guide"
            >
              <HelpCircle size={20} className="text-blue-600" />
            </motion.button>
          </div>

          {/* Currencies and Logout (right side) */}
          <div className="flex items-center gap-2">
            {/* Primos */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-1 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200"
              title="Primos"
            >
              <Sparkles size={12} className="text-amber-500" />
              <span className="text-pocket-text font-bold text-xs">
                {primos.toLocaleString()}
              </span>
            </motion.div>

            {/* Limit Break Shards */}
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-1 bg-gradient-to-r from-purple-50 to-pink-50 px-2.5 py-1 rounded-full border border-purple-200"
              title="Limit Break Shards"
            >
              <Diamond size={12} className="text-purple-500" />
              <span className="text-pocket-text font-bold text-xs">
                {(limitBreakShards || 0).toLocaleString()}
              </span>
            </motion.div>

            {/* Logout */}
            <button
              onClick={logout}
              className="p-2 text-gray-400 hover:text-pocket-red hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </motion.header>

      {/* Main Content */}
      <motion.main
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="p-4 pb-8 max-w-lg mx-auto"
      >
        {/* Welcome Card */}
        <motion.div
          variants={itemVariants}
          className="bg-white rounded-2xl p-5 mb-5 shadow-card"
        >
          <h1 className="text-lg font-bold text-pocket-text mb-1">
            Welcome back, {user.username}!
          </h1>
          <p className="text-pocket-text-light text-sm">
            Pick a buddy and prove you're the very best!
          </p>
        </motion.div>

        {/* Main Menu Grid */}
        <motion.div
          variants={containerVariants}
          className="grid grid-cols-3 gap-3 mb-5"
        >
          {menuItems.map((item) => (
            <motion.div key={item.key} variants={itemVariants} className={item.key === 'career' ? 'tutorial-new-career' : ''}>
              <MenuTile
                icon={item.icon}
                iconColor={item.color}
                label={item.label}
                count={item.countKey ? counts[item.countKey] : undefined}
                onClick={() => {
                  // If on new-career-prompt step and clicking career, advance tutorial
                  if (item.key === 'career' && isTutorialStep('new-career-prompt')) {
                    advanceStep('inspiration-intro');
                  }
                  setGameState(item.screen);
                }}
                disabled={(item.key === 'career' && pokemonInventory.length === 0) || (item.key === 'career' && isLoadingCareer)}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Version + Debug Button */}
        <motion.div
          variants={itemVariants}
          className="flex items-center justify-center gap-3 mt-2"
        >
          <p className="text-pocket-text-light text-[10px]">
            v{packageJson.version}
          </p>
          <button
            onClick={() => setGameState('debugSprites')}
            className="p-1.5 bg-gray-200 rounded text-gray-600 hover:bg-pocket-red hover:text-white transition-colors"
            title="Debug Sprite Tuner"
          >
            <Wrench size={16} />
          </button>
        </motion.div>
      </motion.main>

      {/* Tutorial overlay for career-goal (shown after starter selection) */}
      {isTutorialStep('career-goal') && (
        <TutorialOverlay
          stepId="career-goal"
          onContinue={handleTutorialContinue}
        />
      )}

      {/* Tutorial overlay for gacha intro (shown after first career ends) */}
      {isTutorialStep('gacha-intro') && (
        <TutorialOverlay
          stepId="gacha-intro"
          onContinue={handleTutorialContinue}
        />
      )}

      {/* Tutorial for new-career-prompt (shown after gacha pulls) */}
      {/* First show overlay, then after dismissal show highlight */}
      {isTutorialStep('new-career-prompt') && !newCareerOverlayDismissed && (
        <TutorialOverlay
          stepId="new-career-prompt"
          onContinue={() => {
            setNewCareerOverlayDismissed(true);
          }}
        />
      )}
      {isTutorialStep('new-career-prompt') && newCareerOverlayDismissed && (
        <TutorialHighlight
          selector=".tutorial-new-career"
          isActive={true}
          onTargetClick={() => {
            advanceStep('inspiration-intro');
          }}
        />
      )}
    </div>
  );
};

export default MenuScreen;
